import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Linkedin, ArrowUpRight } from "lucide-react";

export const LINKEDIN_URL = "https://www.linkedin.com/in/jocelyn-macdonald-0a375653/";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Work With Jocelyn", path: "/work-with-jocelyn" },
  { label: "Experience", path: "/experience" },
  { label: "Contact", path: "/contact" },
];

export const Monogram = ({ className = "h-9 w-9" }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#181722" />
    <text x="32" y="43" fontFamily="Georgia, serif" fontStyle="italic" fontSize="30" fill="#6F4A9E" textAnchor="middle">
      JM
    </text>
  </svg>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-bone/85 border-b border-soot/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
          <Monogram className="h-9 w-9 transition-transform duration-500 group-hover:rotate-[-8deg]" />
          <span className="font-serif text-lg sm:text-xl font-semibold tracking-tight text-ink">
            Jocelyn MacDonald
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-sweep text-[11px] font-sans font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-clay" : "text-soot/70 hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-lets-talk"
            className="ml-2 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-clay"
          >
            Let's Talk
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-button"
          className="lg:hidden p-2 text-ink"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-bone border-t border-soot/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-3xl ${isActive ? "text-clay italic" : "text-ink"}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-lets-talk"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-sans font-bold uppercase tracking-[0.2em] text-bone"
              >
                Let's Talk
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-ink text-[#F5F5F7]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
      <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <Monogram className="h-10 w-10" />
            <p className="font-serif text-2xl font-semibold tracking-tight">Jocelyn MacDonald</p>
          </div>
          <p className="mt-4 text-sm text-[#F5F5F7]/60 leading-relaxed max-w-sm">
            People · Performance · Experience
            <br />
            Coach · Consultant · Experience Leader
          </p>
        </div>
        <div>
          <p className="text-[11px] font-sans font-bold uppercase tracking-[0.25em] text-clay mb-5">Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="link-sweep text-sm text-[#F5F5F7]/75 hover:text-[#F5F5F7] transition-colors"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[11px] font-sans font-bold uppercase tracking-[0.25em] text-clay mb-5">Connect</p>
          <ul className="space-y-3">
            <li>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-link-linkedin"
                className="inline-flex items-center gap-2 text-sm text-[#F5F5F7]/75 hover:text-[#F5F5F7] transition-colors"
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
            </li>
            <li>
              <Link to="/contact" data-testid="footer-link-contact" className="text-sm text-[#F5F5F7]/75 hover:text-[#F5F5F7] transition-colors link-sweep">
                Contact
              </Link>
            </li>
            <li>
              <Link to="/privacy" data-testid="footer-link-privacy" className="text-sm text-[#F5F5F7]/75 hover:text-[#F5F5F7] transition-colors link-sweep">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#F5F5F7]/45">
        <p>© 2026 Jocelyn MacDonald. All rights reserved.</p>
        <p className="font-hand text-base text-[#F5F5F7]/55">Empress Coaching &amp; Consulting</p>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

export default Layout;
